import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Sparkles, Eye, EyeOff, Lock, User, AlertCircle, UserX } from 'lucide-react';
import './Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [anotherAdminLoggedIn, setAnotherAdminLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const isMountedRef = useRef(true);

  const { login, isAuthenticated } = useAuth();

  // Redirect if already authenticated (e.g. page refresh while logged in)
  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated]);

  // Redirect if already authenticated (e.g. page refresh while logged in)
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = useCallback(async (e, force = false) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setAnotherAdminLoggedIn(false);

    try {
      const result = await login(username, password, force);
      if (!isMountedRef.current) return;
      if (result.success) {
        // Hide login page immediately
        setIsLoggingIn(true);
        // Full page navigation to guarantee login page removal
        // (consistent with logout in Header.js)
        window.location.href = '/';
        return;
      } else if (result.code === 'ANOTHER_ADMIN_LOGGED_IN') {
        setAnotherAdminLoggedIn(true);
        setError(result.error);
      } else {
        setError(result.error || 'Неверный логин или пароль');
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError('Ошибка подключения к серверу');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [username, password, login]);

  const handleForceLogin = (e) => {
    handleSubmit(e, true);
  };

  if (isLoggingIn) return null;

  return (
    <div className="login-page">
      {/* Video background removed */}

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <Sparkles size={32} />
            </div>
            <h1 className="login-title">Ballet Post</h1>
            <p className="login-subtitle">Панель управления</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
            {error && (
              <div className={`login-error ${anotherAdminLoggedIn ? 'login-error-warning' : ''}`}>
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}
            
            {anotherAdminLoggedIn && (
              <button 
                type="button"
                className="kick-admin-button"
                onClick={handleForceLogin}
                disabled={loading}
              >
                <UserX size={18} />
                Выкинуть другого админа и войти
              </button>
            )}

            <div className="input-group">
              <label htmlFor="username" className="input-label">Логин</label>
              <div className="input-wrapper">
                <User size={18} className="input-icon" />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Введите логин"
                  className="input"
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">Пароль</label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  className="input"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <span className="button-loading">
                  <span className="spinner-sm"></span>
                  Вход...
                </span>
              ) : (
                'Войти в систему'
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Версия 3.0.0 | © 2024 Ballet Post</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
